// A ball that plays back a trajectory the server computed. One per player. `play`, `update`,
// `setPosition` and `position` are the contract; the trail, the contact shadow and the landing puff
// are the visuals. Give it the hole (`ball.hole = hole`) and the shadow and the puff sit on the
// ground properly — without it they fall back to the sample's own y.

import * as THREE from 'three';
import { sampleHeight, sampleSurface, type Hole, type Surface } from '@shared/course';
import type { ShotSample } from '@shared/physics';

const AIRBORNE_M = 0.55;

export class Ball {
  mesh: THREE.Mesh;
  playing = false;
  /** set per hole so height/surface lookups work; optional */
  hole: Hole | null = null;
  samples: ShotSample[] = [];
  private scene: THREE.Object3D;
  private shadow: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>;
  private trail: Trail;
  private puff: Puff;
  private startedAt = 0;
  private onDone: (() => void) | null = null;
  private airborne = false;

  constructor(scene: THREE.Object3D, color = 0xffffff) {
    this.scene = scene;
    this.mesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.09, 24, 16),
      new THREE.MeshStandardMaterial({ color, roughness: 0.32, metalness: 0.02 }),
    );
    this.mesh.castShadow = true;
    this.mesh.name = 'ball';
    scene.add(this.mesh);

    // A contact shadow the eye can read: the sun's shadow map is too coarse at 9 cm, and height
    // above the turf is the only cue that tells you where a ball in flight will land.
    this.shadow = new THREE.Mesh(
      new THREE.CircleGeometry(0.16, 24),
      new THREE.MeshBasicMaterial({ color: 0x101408, transparent: true, opacity: 0.4, depthWrite: false }),
    );
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.renderOrder = 2;
    scene.add(this.shadow);

    this.trail = new Trail(scene, color);
    this.puff = new Puff(scene);
  }

  get position() { return this.mesh.position; }

  setHole(hole: Hole) { this.hole = hole; }

  /** A holed ball is in the cup, not sitting on it: hidden until the next hole shows it again. */
  setVisible(on: boolean) { this.mesh.visible = on; this.shadow.visible = on; }

  setPosition(x: number, y: number, z: number) {
    this.mesh.position.set(x, y + 0.09, z);
    const g = this.hole ? sampleHeight(this.hole, x, z) : y;
    this.shadow.position.set(x, g + 0.02, z);
    const k = Math.min(1, Math.max(0, y - g) / 18);
    this.shadow.scale.setScalar(1 + k * 2.4);
    this.shadow.material.opacity = 0.42 * (1 - k * 0.8);
  }

  play(samples: ShotSample[], onDone?: () => void) {
    if (!samples || samples.length < 2) {
      const s = samples?.[0];
      if (s) this.setPosition(s.x, s.y, s.z);
      onDone?.();
      return;
    }
    this.samples = samples;
    this.startedAt = performance.now();
    this.playing = true;
    this.onDone = onDone ?? null;
    this.trail.reset();
    this.airborne = false;
  }

  /** Call every frame. Interpolates the server's samples in real time. */
  update() {
    this.trail.update(this.scene);
    this.puff.update();
    if (!this.playing) return;
    const t = (performance.now() - this.startedAt) / 1000;
    const s = this.samples;
    const last = s[s.length - 1];
    if (t >= last.t) {
      this.setPosition(last.x, last.y, last.z);
      this.playing = false;
      this.trail.fade();
      const cb = this.onDone; this.onDone = null; cb?.();
      return;
    }
    let i = 1;
    while (i < s.length - 1 && s[i].t < t) i++;
    const a = s[i - 1], b = s[i];
    const f = b.t === a.t ? 0 : (t - a.t) / (b.t - a.t);
    const x = a.x + (b.x - a.x) * f, y = a.y + (b.y - a.y) * f, z = a.z + (b.z - a.z) * f;
    this.setPosition(x, y, z);

    const g = this.hole ? sampleHeight(this.hole, x, z) : 0;
    const air = y - g > AIRBORNE_M;
    if (this.airborne && !air) {
      const surf: Surface = this.hole ? sampleSurface(this.hole, x, z) : 'fairway';
      this.puff.burst(x, g, z, surf);
    }
    this.airborne = air;
    this.trail.push(x, y + 0.09, z, y - g > 0.3);
  }

  dispose() {
    this.scene.remove(this.mesh, this.shadow);
    this.trail.dispose();
    this.puff.dispose();
  }
}

/** A camera-facing ribbon through the last ~90 flight positions, widening and brightening forward. */
class Trail {
  private max = 90;
  private pts: { x: number; y: number; z: number }[] = [];
  private geo = new THREE.BufferGeometry();
  private mat: THREE.MeshBasicMaterial;
  private mesh: THREE.Mesh;
  private color: THREE.Color;
  private fading = false;
  private a = new THREE.Vector3();
  private b = new THREE.Vector3();
  private c = new THREE.Vector3();
  private scene: THREE.Object3D;

  constructor(scene: THREE.Object3D, color: number) {
    this.scene = scene;
    this.geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.max * 2 * 3), 3));
    this.geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(this.max * 2 * 3), 3));
    const idx: number[] = [];
    for (let i = 0; i < this.max - 1; i++) idx.push(i * 2, i * 2 + 1, i * 2 + 2, i * 2 + 1, i * 2 + 3, i * 2 + 2);
    this.geo.setIndex(idx);
    this.mat = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.75, side: THREE.DoubleSide, depthWrite: false });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    this.mesh.visible = false;
    this.color = new THREE.Color(color);
    scene.add(this.mesh);
  }

  reset() { this.pts = []; this.fading = false; this.mesh.visible = true; this.mat.opacity = 0.75; }
  fade() { this.fading = true; }

  push(x: number, y: number, z: number, airborne: boolean) {
    if (!airborne && this.pts.length === 0) return;
    const p = this.pts[this.pts.length - 1];
    if (p && Math.hypot(p.x - x, p.y - y, p.z - z) < 0.35) return;
    this.pts.push({ x, y, z });
    while (this.pts.length > this.max) this.pts.shift();
  }

  update(scene: THREE.Object3D) {
    if (this.fading) {
      this.mat.opacity *= 0.955;
      if (this.mat.opacity < 0.02) { this.mesh.visible = false; this.fading = false; this.pts = []; }
    }
    const n = this.pts.length;
    if (n < 2) { this.geo.setDrawRange(0, 0); return; }
    const cam = scene.userData.camera as THREE.Camera | undefined;
    const pos = this.geo.attributes.position as THREE.BufferAttribute;
    const cols = this.geo.attributes.color as THREE.BufferAttribute;
    for (let i = 0; i < n; i++) {
      const p = this.pts[i];
      const q = this.pts[Math.min(n - 1, i + 1)];
      const r = this.pts[Math.max(0, i - 1)];
      this.a.set(q.x - r.x, q.y - r.y, q.z - r.z);
      if (this.a.lengthSq() < 1e-8) this.a.set(0, 0, 1);
      this.a.normalize();
      if (cam) this.b.copy(cam.position).sub(this.c.set(p.x, p.y, p.z)).normalize();
      else this.b.set(0, 1, 0);
      this.c.crossVectors(this.a, this.b).normalize();
      const f = i / (n - 1);
      const w = 0.035 + 0.075 * f;
      pos.setXYZ(i * 2, p.x - this.c.x * w, p.y - this.c.y * w, p.z - this.c.z * w);
      pos.setXYZ(i * 2 + 1, p.x + this.c.x * w, p.y + this.c.y * w, p.z + this.c.z * w);
      const alpha = Math.pow(f, 1.6);
      cols.setXYZ(i * 2, this.color.r * alpha, this.color.g * alpha, this.color.b * alpha);
      cols.setXYZ(i * 2 + 1, this.color.r * alpha, this.color.g * alpha, this.color.b * alpha);
    }
    pos.needsUpdate = true;
    cols.needsUpdate = true;
    this.geo.setDrawRange(0, (n - 1) * 6);
  }

  dispose() { this.scene.remove(this.mesh); this.geo.dispose(); this.mat.dispose(); }
}

const PUFF_TINT: Record<Surface, number> = {
  tee: 0xd9e3c4, fairway: 0xd9e3c4, green: 0xdfe9cc, rough: 0xb9c49a,
  sand: 0xe8d7ab, water: 0xdfefff, oob: 0xc9c2a0,
};

/** The landing: eighteen camera-facing sprites, tinted by what the ball hit. */
class Puff {
  private n = 18;
  private mat: THREE.MeshBasicMaterial;
  private mesh: THREE.InstancedMesh;
  private parts: { x: number; y: number; z: number; vx: number; vy: number; vz: number; s: number; life: number }[] = [];
  private t = 0;
  private scene: THREE.Object3D;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private s = new THREE.Vector3();

  constructor(scene: THREE.Object3D) {
    this.scene = scene;
    this.mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false });
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.5, 0.5), this.mat, this.n);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    this.mesh.renderOrder = 3;
    scene.add(this.mesh);
  }

  burst(x: number, y: number, z: number, surface: Surface) {
    this.parts = Array.from({ length: this.n }, () => {
      const a = Math.random() * Math.PI * 2, sp = 0.6 + Math.random() * 2.2;
      return {
        x, y: y + 0.05, z,
        vx: Math.cos(a) * sp, vy: 0.8 + Math.random() * 2.2, vz: Math.sin(a) * sp,
        s: 0.2 + Math.random() * 0.45, life: 0.5 + Math.random() * 0.45,
      };
    });
    this.mat.color.set(PUFF_TINT[surface] ?? 0xd9e3c4);
    this.t = 0;
    this.mesh.visible = true;
  }

  update() {
    if (!this.mesh.visible) return;
    const dt = 1 / 60;
    this.t += dt;
    const cam = this.scene.userData.camera as THREE.Camera | undefined;
    let alive = 0;
    this.parts.forEach((p, i) => {
      p.life -= dt;
      if (p.life <= 0) {
        this.m.compose(this.v.set(0, -999, 0), this.q, this.s.set(0, 0, 0));
        this.mesh.setMatrixAt(i, this.m);
        return;
      }
      alive++;
      p.vy -= 5.5 * dt; p.vx *= 0.94; p.vz *= 0.94;
      p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt;
      if (cam) this.q.copy(cam.quaternion);
      this.s.setScalar(p.s * (1 + (0.9 - p.life) * 1.6));
      this.m.compose(this.v.set(p.x, p.y, p.z), this.q, this.s);
      this.mesh.setMatrixAt(i, this.m);
    });
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mat.opacity = Math.max(0, 0.55 - this.t * 0.7);
    if (!alive || this.mat.opacity <= 0.01) this.mesh.visible = false;
  }

  dispose() { this.scene.remove(this.mesh); this.mesh.dispose(); this.mat.dispose(); }
}
